import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, ReactNode } from 'react';

import { cn } from '@/lib/utils';

/**
 * Alert — an inline, non-dismissable message block (05 §6.3).
 *
 * Colour is never the only signal: the variant tints the border and the icon
 * slot, but the title and body always carry the meaning in words.
 */
export const alertVariants = cva('rounded-lg border p-4 text-sm', {
  variants: {
    variant: {
      info: 'border-border bg-muted/40 text-foreground',
      success: 'border-success/40 bg-success/10 text-foreground',
      warning: 'border-warning/50 bg-warning/10 text-foreground',
      destructive: 'border-destructive/50 bg-destructive/10 text-foreground',
    },
  },
  defaultVariants: { variant: 'info' },
});

export interface AlertProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'title'>, VariantProps<typeof alertVariants> {
  title?: ReactNode;
  /** Rendered at the inline end — a retry button, say. */
  action?: ReactNode;
  children?: ReactNode;
}

export function Alert({ className, variant, title, action, children, ...props }: AlertProps) {
  // `destructive` and `warning` are things the user must notice now, so they are
  // announced; `info`/`success` are polite.
  const assertive = variant === 'destructive' || variant === 'warning';

  return (
    <div
      role={assertive ? 'alert' : 'status'}
      aria-live={assertive ? 'assertive' : 'polite'}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    >
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          {title ? <p className="font-medium">{title}</p> : null}
          {children ? (
            <div className={cn('text-muted-foreground', title ? 'mt-1' : undefined)}>
              {children}
            </div>
          ) : null}
        </div>
        {action ? <div className="shrink-0">{action}</div> : null}
      </div>
    </div>
  );
}

export const AlertTitle = ({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) => (
  <p className={cn('font-medium', className)} {...props} />
);

export const AlertDescription = ({ className, ...props }: HTMLAttributes<HTMLDivElement>) => (
  <div className={cn('mt-1 text-muted-foreground', className)} {...props} />
);
